import keycloakApi from './keycloak-auth';

// Personal Information
export interface PersonalInfo {
  firstName: string;
  lastName: string;
  name: string;
  email: string;
  phoneNumber?: string;
}

export interface UpdatePersonalInfoRequest {
  phoneNumber?: string;
}

// Password Update
export interface UpdatePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

// Business ACH Information
export interface BusinessAchInfo {
  routingNumber: string; // Masked by default (****1234)
  accountNumber: string; // Masked by default (****5678)
  accountType?: string;
  hasBankAccount: boolean;
}

export interface UpdateBusinessAchRequest {
  routingNumber: string; // Full 9-digit number
  accountNumber: string; // Full account number
  accountType?: 'CHECKING' | 'SAVINGS' | undefined;
}

export interface RevealAchResponse {
  routingNumber: string; // Full number
  accountNumber: string; // Full number
  accountType?: string;
}

// Party Information
export interface PartyInfo {
  id: string;
  name: string;
  address?: string; // Full formatted address (for display)
  address1?: string;
  address2?: string;
  city?: string;
  stateCode?: string;
  postalCode?: string;
  email?: string;
  phone?: string;
  autoApprovePaymentFiles?: boolean;
  allowedPaymentFileUploadTypes?: Array<'ui_batch' | 'sftp'>;
  logoUrl?: string;
}

export interface UpdatePartyInfoRequest {
  name: string;
  address1?: string;
  address2?: string;
  city?: string;
  stateCode?: string;
  postalCode?: string;
  email?: string;
  phone?: string;
  autoApprovePaymentFiles?: boolean;
  allowedPaymentFileUploadTypes?: Array<'ui_batch' | 'sftp'>;
  // Address verification fields
  addressVerificationStatus?: 'verified' | 'not_verified' | 'not_checked';
  addressVerifiedAt?: string; // ISO date string
}

// Complete Profile
export interface ProfileResponse {
  personalInfo: PersonalInfo;
  businessInfo: BusinessAchInfo;
  partyInfo: PartyInfo;
}

const apiCall = async <T>(endpoint: string, options: RequestInit = {}): Promise<T> => {
  return keycloakApi.authenticatedApiCall<T>(endpoint, options);
};

/**
 * Get complete user profile (personal + business info)
 */
export const getProfile = async (): Promise<ProfileResponse> => {
  return apiCall<ProfileResponse>('/settings', {
    method: 'GET',
  });
};

/**
 * Get list of states/provinces for a country
 * @param countryCode - ISO 3166-1 alpha-2 country code (defaults to 'US')
 */
export const getStates = async (
  countryCode: string = 'US'
): Promise<Array<{ code: string; name: string }>> => {
  // apiCall already unwraps { success: true, data: [...] } and returns just the data
  return apiCall<Array<{ code: string; name: string }>>(`/settings/states?country=${countryCode}`, {
    method: 'GET',
  });
};

/**
 * Update personal information (phone number)
 */
export const updatePersonalInfo = async (
  data: UpdatePersonalInfoRequest
): Promise<{ success: boolean; message: string }> => {
  return apiCall('/settings/personal', {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
};

/**
 * Update user password
 */
export const updatePassword = async (
  data: UpdatePasswordRequest
): Promise<{ success: boolean; message: string }> => {
  return apiCall('/settings/password', {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
};

/**
 * Update business ACH information
 * Note: apiCall automatically unwraps { success, data } responses
 */
export const updateBusinessAch = async (
  data: UpdateBusinessAchRequest
): Promise<BusinessAchInfo> => {
  return apiCall<BusinessAchInfo>('/settings/business-ach', {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
};

/**
 * Update party information (company details)
 * Note: apiCall automatically unwraps { success, data } responses
 */
export const updatePartyInfo = async (data: UpdatePartyInfoRequest): Promise<PartyInfo> => {
  return apiCall<PartyInfo>('/settings/party', {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
};

/**
 * Reveal full ACH numbers (requires authentication)
 */
export const revealAchNumbers = async (): Promise<RevealAchResponse> => {
  return apiCall<RevealAchResponse>('/settings/reveal-ach', {
    method: 'POST',
  });
};

/**
 * Get supplier email toggle setting
 */
export const getSupplierEmailToggle = async (): Promise<{ enabled: boolean }> => {
  return apiCall<{ enabled: boolean }>('/settings/supplier-emails-toggle', {
    method: 'GET',
  });
};

/**
 * Update supplier email toggle setting
 */
export const updateSupplierEmailToggle = async (
  enabled: boolean
): Promise<{ enabled: boolean }> => {
  return apiCall<{ enabled: boolean }>('/settings/supplier-emails-toggle', {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ enabled }),
  });
};

/**
 * Upload company logo
 */
export const uploadCompanyLogo = async (file: File): Promise<{ logoUrl: string }> => {
  const formData = new FormData();
  formData.append('logo', file);
  return apiCall<{ logoUrl: string }>('/settings/party/logo', {
    method: 'POST',
    body: formData,
  });
};

/**
 * Delete company logo
 */
export const deleteCompanyLogo = async (): Promise<void> => {
  await apiCall<void>('/settings/party/logo', {
    method: 'DELETE',
  });
};
